import React, {useRef, useState} from 'react';
import {Checkbox} from '@mui/material';
import Autocomplete from '@/components/autocomplete';
import {Option} from '@/components/autocomplete/Autocomplete';
import useParams from '@/hooks/use-params';
import styles from './CampaignFilter.module.scss';

export type CampaignFilterProps = {
  testID?: string;
  options: Option[];
  onChange: (event: any, selectedOptions: Option[]) => void;
};

const ALL_OPTION: Option = {id: 'all', label: 'All Campaigns'};

function CampaignFilter(props: CampaignFilterProps) {
  const {campaign} = useParams();

  const {testID = 'campaign-filter', options, onChange} = props;

  const isAllSelected = !campaign || campaign === 'all';

  // Local pending state — only committed to URL on dropdown close
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);
  const hasPendingRef = useRef(false);

  const campaignOptions = [ALL_OPTION, ...options];

  // Active selection: pending if open, otherwise from URL
  const getActiveIds = () => {
    if (pendingIds !== null) return pendingIds;
    if (isAllSelected) return ['all'];
    return String(campaign).split(',');
  };
  const activeIds = getActiveIds();

  const selectedOptions = (() => {
    if (activeIds.includes('all')) return [ALL_OPTION];
    const matched = options.filter((opt) => activeIds.includes(String(opt.id)));
    // Fall back to All if URL IDs don't match any available options (e.g. brand switch)
    return matched.length === 0 ? [ALL_OPTION] : matched;
  })();

  const getDisplayValue = () => {
    if (activeIds.includes('all')) return 'All Campaigns';
    if (selectedOptions.length === 1) return selectedOptions[0].label || '';
    return `${selectedOptions.length} campaigns selected`;
  };
  const displayValue = getDisplayValue();

  // Committed IDs from URL (not pending state)
  const committedIds = isAllSelected ? ['all'] : String(campaign).split(',');

  const handleChange = (_event: any, newValue: Option[]) => {
    const nowHasAll = newValue.some((opt) => opt.id === 'all');
    const prevHadAll = activeIds.includes('all');

    let nextIds: string[];
    if (nowHasAll && !prevHadAll) {
      nextIds = ['all'];
    } else if (prevHadAll && newValue.length > 1) {
      nextIds = newValue
        .filter((opt) => opt.id !== 'all')
        .map((opt) => String(opt.id));
    } else {
      const filtered = newValue.filter((opt) => opt.id !== 'all');
      nextIds =
        filtered.length === 0 ? ['all'] : filtered.map((opt) => String(opt.id));
    }

    setPendingIds(nextIds);
    hasPendingRef.current = true;
  };

  const handleClose = () => {
    if (!hasPendingRef.current || pendingIds === null) {
      return;
    }
    hasPendingRef.current = false;

    const ids = pendingIds;
    setPendingIds(null);

    // Skip if selection hasn't actually changed
    const sortedPending = [...ids].sort();
    const sortedCommitted = [...committedIds].sort();
    if (
      sortedPending.length === sortedCommitted.length &&
      sortedPending.every((id, i) => id === sortedCommitted[i])
    ) {
      return;
    }

    if (ids.includes('all') || ids.length === 0) {
      onChange(null, [ALL_OPTION]);
    } else {
      const selected = options.filter((opt) => ids.includes(String(opt.id)));
      onChange(null, selected);
    }
  };

  return (
    <div className={styles.container} data-testid={testID}>
      <Autocomplete
        testID={`${testID}--autocomplete`}
        multiple
        disableClearable
        disableCloseOnSelect
        adaptiveWidth={false}
        options={campaignOptions}
        showLabel={false}
        customPopperStyle={styles.popper}
        value={selectedOptions}
        onChange={handleChange}
        onClose={handleClose}
        renderOption={(optionProps: any, option: Option, baseStyle: string) => {
          const isChecked = activeIds.includes(String(option.id));
          return (
            <li
              {...optionProps}
              className={`${optionProps.className} ${baseStyle}`}
              key={option.id}
            >
              <Checkbox checked={isChecked} size="small" sx={{mr: 0.5, p: 0}} />
              {option.label}
            </li>
          );
        }}
        slotProps={{popper: {placement: 'bottom-start'}}}
        filterSelectedOptions={false}
        sx={{
          '.MuiFormControl-root': {
            background: '#f4f4f4',
            paddingInline: 1.5,
            borderRadius: 15,
            minWidth: 180,
          },
          '.MuiChip-root': {display: 'none'},
          'input': {minWidth: '0 !important'},
        }}
        placeholder={displayValue}
        listboxProps={{'data-testid': `${testID}--list-box`}}
      />
    </div>
  );
}

export default CampaignFilter;
